import { Field } from '@campfire/field/lib';
import { TabletButton } from '@campfire/tablet-button/lib';
import { Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography } from '@material-ui/core';
import { FieldArrayRenderProps, Form, Formik, useFormikContext } from 'formik';
import React, { memo, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { object as YupObject, string as YupString } from 'yup';
import { FormBuilderValues } from '../FormBuilder';
import { GetForm_vm_form_formItems_VOLUNTEER_FormItemHeadingType as HeadingFormItemType } from '../../__generated__/GetForm';

const validationSchema = YupObject().shape({
  heading: YupString().required('Please provide a heading name'),
});

interface Props {
  open: boolean;
  close: () => void;
  arrayHelpers: FieldArrayRenderProps;
  data: HeadingFormItemType | null;
}

type HeadingValues = {
  formItemId: string;
  heading: string;
  order: number;
};

export const HeadingFormItemDialog = memo((props: Props) => {
  const { open, close, data } = props;
  const formikContext = useFormikContext<FormBuilderValues>();
  const { values } = formikContext;
  const { formItems } = values;

  const title = useMemo(() => (data ? 'Edit Heading' : 'Add Heading'), [data]);

  const initialValues: HeadingValues = useMemo(
    () => ({
      heading: data?.heading ?? '',
      order: data?.order ?? formikContext.values.formItems.allIds.length,
      formItemId: data?.formItemId ?? `new-${uuidv4()}`,
      __typename: 'VOLUNTEER_FormItemHeadingType',
    }),
    [data, open]
  );

  const handleSubmit = (vals: HeadingValues) => {
    return data ? editHeading(vals) : addHeading(vals);
  };

  const addHeading = (vals: HeadingValues) => {
    formikContext.setFieldValue('formItems', {
      ...formItems,
      allIds: formItems.allIds.concat(vals.formItemId),
      byId: {
        ...formItems.byId,
        [vals.formItemId]: vals,
      },
    });
    close();
  };

  const editHeading = (vals: HeadingValues) => {
    formikContext.setFieldValue('formItems', {
      ...formItems,
      byId: {
        ...formItems.byId,
        [vals.formItemId]: vals,
      },
    });
    close();
  };

  const removeHeading = () => {
    const optionId = formItems.byTriggerOption[initialValues.formItemId];
    formikContext.setFieldValue('formItems', {
      ...formItems,
      allIds: formItems.allIds.filter((itemId) => itemId !== initialValues.formItemId),
      byTrigger: optionId
        ? formItems.byTrigger[optionId].triggerItemIds.filter((itemId) => itemId !== initialValues.formItemId)
        : formItems.byTrigger,
    });
    close();
  };

  return (
    <Dialog open={open} maxWidth='sm' fullWidth>
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
        {({ submitForm }) => (
          <Form>
            <DialogTitle>
              <Typography variant='h6'>{title}</Typography>
              <Typography variant='body2' color='textSecondary'>
                {'Use headings to break your form up into smaller sections'}
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Field name='heading' label='Heading name' required fullWidth variant='outlined' helper autoFocus />
            </DialogContent>
            <DialogActions>
              <Grid container justify='flex-end'>
                {data ? (
                  <Grid item xs>
                    <TabletButton variant='outlined' color='error' onClick={removeHeading}>
                      {'Delete'}
                    </TabletButton>
                  </Grid>
                ) : null}
                <Grid item>
                  <TabletButton onClick={close}>{'Cancel'}</TabletButton>
                </Grid>
                <Grid item>
                  <TabletButton type='button' color='primary' variant='contained' onClick={submitForm}>
                    {'Done'}
                  </TabletButton>
                </Grid>
              </Grid>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
});
